import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ContactForm.css';

const ContactForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '', // Phone is now required
    comment: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.comment.trim()) newErrors.comment = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.post(`${apiUrl}/api/contact`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', comment: '' });
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle click outside the modal to close it
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent touch events from bubbling up
  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div 
      className={`contact-form-overlay ${isVisible ? 'visible' : ''}`}
      onClick={handleOverlayClick}
      onTouchMove={handleTouchMove}
    >
      <div className="contact-form-container" onClick={(e) => e.stopPropagation()}>
        <button 
          className="close-button" 
          onClick={handleCloseClick}
          aria-label="Close contact form"
        >×</button>
        <h2>Get in Touch</h2>

        {submitStatus === 'success' ? (
          <div className="status-message success-message">
            <svg viewBox="0 0 24 24" className="status-icon">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
            <h3>Thank you!</h3>
            <p>Your message has been sent successfully.</p>
          </div>
        ) : submitStatus === 'error' ? (
          <div className="status-message error-message">
            <svg viewBox="0 0 24 24" className="status-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <h3>Oops!</h3>
            <p>Something went wrong. Please try again later.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="animated-form">
            <div className="form-group">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder=" "
              />
              <label htmlFor="name" className="form-label">Name *</label>
              <div className="form-underline"></div>
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder=" "
              />
              <label htmlFor="email" className="form-label">Email *</label>
              <div className="form-underline"></div>
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder=" "
              />
              <label htmlFor="phone" className="form-label">Mobile Number *</label>
              <div className="form-underline"></div>
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
            
            <div className="form-group">
              <textarea
                id="comment"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                required
                className={`form-input ${errors.comment ? 'error' : ''}`}
                rows="4"
                placeholder=" "
              />
              <label htmlFor="comment" className="form-label">Message *</label>
              <div className="form-underline"></div>
              {errors.comment && <span className="error-message">{errors.comment}</span>}
            </div>
            
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span> Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactForm;